import {Money} from "./Money.js";
import {ObservableField, MoneyField} from "./MoneyField.js";


/**
 * This class can sum multiple money field into one total field (provided in constructor)
 */
export class SumFields extends ObservableField {
	/**
	 * @param {string|jQuery} amount - DOM ID of input with sum amount or jQuery representing that input
	 * @param {string} [currency] - code of the currency, used in calculations, defaults to system default
	 */
	constructor(amount, currency) {
		super();
		this.amountInput = typeof amount === 'string' ? $('#' + amount) : amount;
		this.amountInput.attr('readonly', 'true');
		/**
		 * @type {MoneyField[]}
		 */
		this.plus = [];
		this.currency = currency ? currency : Server.defaultCurrency;
		this.myName = "";
	}

	named(name) {
		this.myName = name;
		return this;
	}

	get name() {
		return this.myName;
	}

	/**
	 * @param {MoneyField} moneyField - field to be monitored for calculating summary
	 * @returns {SumFields} - returns self
	 */
	add(moneyField) {
		this.plus.push(moneyField);
		moneyField.onChange(this.trigger.bind(this));
		return this;
	}

	/**
	 * @return {Money} - summary as Money class
	 */
	toMoney() {
		return Money.of(Money.parse(this.amountInput.val()), this.currency);
	}

	trigger() {
		this.update();
		super.trigger();
	}

	/**
	 * Update target input field with summary
	 */
	update() {
		let sum = new Money(0, this.currency);
		for (let addition of this.plus) {
			sum = sum.add(addition.toMoney().convertTo(this.currency));
		}
		this.amountInput.val(sum.format());
	}
}


/**
 * This class can dynamically calculate surcharge (margin) from purchase and selling prices
 */
export class SurchargeField extends ObservableField {
	/**
	 * @param {MoneyTaxField} purchase
	 * @param {MoneyTaxField} sale
	 * @param {string|jQuery} surcharge
	 * @param {string|jQuery} fixSurcharge - checkbox for switching recalc mode (fixed surcharge)
	 * @param {boolean} correctSurcharge - if true, surcharge will be updated on init, if false, nothing happen
	 */
	constructor(purchase, sale, surcharge, fixSurcharge, correctSurcharge) {
		super();
		this.purchase = purchase;
		this.sale = sale;
		this.surcharge = typeof surcharge === 'string' ? $('#' + surcharge) : surcharge;
		this.fixSurcharge = typeof fixSurcharge === 'string' ? $('#' + fixSurcharge) : fixSurcharge;

		this.purchase.onChange(this.triggerOnPurchasePriceChange.bind(this));
		this.sale.onChange(this.triggerOnSalePriceChange.bind(this));
		this.surcharge.on('input', this.triggerOnSurchargeChange.bind(this));
		if (correctSurcharge) this.update(true, false);
	}

	triggerOnPurchasePriceChange() {
		this.update(true, true);
		super.trigger();
	}

	triggerOnSalePriceChange() {
		this.update(true, false);
		super.trigger();
	}

	triggerOnSurchargeChange() {
		this.update(false);
		this.checkNegative();
		super.trigger();
	}

	checkNegative() {
		const val = Money.parse(this.surcharge.val());
		if (val < 0) this.surcharge.addClass("neg");
		else this.surcharge.removeClass("neg");
	}

	/**
	 * Update target input field with summary
	 * @param {boolean} priceChange	- true if price was changed, false if surcharge was changed
	 * @param {boolean} [isPurchasePrice] - if priceChange param is true then this boolean determine kind of changed price
	 */
	update(priceChange, isPurchasePrice) {
		let updateSurcharge = priceChange && (!isPurchasePrice || !this.fixSurcharge.is(":checked"));
		if (updateSurcharge) {
			let purchaseMoney, saleMoney;
			if (this.purchase.state.vatBase) {
				purchaseMoney = this.purchase.priceTax.toMoney();
				saleMoney = this.sale.priceTax.toMoney();
			} else {
				purchaseMoney = this.purchase.price.toMoney();
				saleMoney = this.sale.price.toMoney();
			}
			purchaseMoney = purchaseMoney.convertTo(saleMoney.currency);	// unify currencies
			let perc;
			if (purchaseMoney.amount === 0) perc = "";
			else perc = Money.formatPercent(saleMoney.amount / purchaseMoney.amount - 1);
			this.surcharge.val(perc);
			this.checkNegative();
		} else {
			const perc = (100 + Money.parse(this.surcharge.val()))/100.0;
			if (this.purchase.state.vatBase) {
				const m = this.purchase.priceTax.toMoney().multiply(perc).convertTo(this.sale.priceTax.currency);
				this.sale.priceTax.amount = m.format();
				this.sale.updateBaseFromTax();
			} else {
				const m = this.purchase.price.toMoney().multiply(perc).convertTo(this.sale.price.currency);
				this.sale.price.amount = m.format();
				this.sale.updateTaxFromBase();
			}
		}
	}
}

